import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  GrcButton,
  GrcButtonStylingMode,
  GrcButtonType,
  GrcPopup,
  GrcTextArea
} from '@grc/ui-package';
import { useI18nState } from '../../../stores/settings/language-context';
import { IApprovalDecisionPopupProps } from './approval-decision-popup.interface';
import './approval-decision-popup.scss';

// One shared popup for both the Approval Notes and Rejection Notes mockups -
// they're structurally identical (title, Comments textarea, Cancel + one
// colored primary action), differing only in copy/color (plan §17b).
export default function ApprovalDecisionPopup({
  visible,
  decisionType,
  quoteIds,
  isSubmitting,
  onCancel,
  onSubmit
}: IApprovalDecisionPopupProps) {
  const { localeData } = useI18nState();
  const [comments, setComments] = useState('');

  useEffect(() => {
    if (visible) {
      setComments('');
    }
  }, [visible, decisionType, quoteIds]);

  const copy = useMemo(
    () =>
      decisionType === 'approve'
        ? {
            title: localeData.ApprovalNotes,
            buttonText: localeData.Approve,
            buttonType: GrcButtonType.Success,
            stylingMode: GrcButtonStylingMode.Contained
          }
        : {
            title: localeData.RejectionNotes,
            buttonText: localeData.Reject,
            buttonType: GrcButtonType.Danger,
            stylingMode: GrcButtonStylingMode.Outlined
          },
    [decisionType, localeData]
  );

  const handleSubmit = useCallback(() => {
    onSubmit(comments);
  }, [onSubmit, comments]);

  return (
    <GrcPopup
      visible={visible}
      onHiding={onCancel}
      showTitle
      title={copy.title}
      showCloseButton
      dragEnabled
      width={600}
      height="auto"
      wrapperAttr={{ 'data-testid': 'approval_decision_popup' }}
    >
      <div className="approval-decision-popup-body">
        <label className="grc-label" htmlFor="approval_decision_comments">
          {localeData.Comments}
        </label>
        <GrcTextArea
          id="approval_decision_comments"
          value={comments}
          onValueChange={(value: string) => setComments(value ?? '')}
          placeholder={localeData.WriteHere}
          height={140}
          stylingMode="outlined"
          disabled={isSubmitting}
        />
      </div>
      <div className="approval-decision-popup-footer d-flex justify-content-end gap-2 mt-4">
        <GrcButton
          id="approval_decision_cancel_button"
          text={localeData.CancelButton}
          stylingMode={GrcButtonStylingMode.Outlined}
          onClick={onCancel}
          disabled={isSubmitting}
        />
        <GrcButton
          id="approval_decision_submit_button"
          text={copy.buttonText}
          type={copy.buttonType}
          stylingMode={copy.stylingMode}
          onClick={handleSubmit}
          disabled={isSubmitting}
        />
      </div>
    </GrcPopup>
  );
}
